<template>
  <div>

    <va-modal
      :value="isOpen"
      ref="recordModal"
      :okText="$t('modals.buttons.save')"
      :okClass="'btn btn-primary btn-micro'"
      :okToClose="false"
      @ok="submit"
      :cancelText="$t('modals.buttons.back')"
      :cancelClass="'btn-cancelar btn btn-primary btn-micro'"
      @cancel="close"
      size="large">

      <!-- Modal Header -->
      <div slot="header" class="modal-title">
        <h4 class="mb-0">
          <b>{{ isUpdate ? $t('ranu.records.update') : $t('ranu.records.create') }}</b>
        </h4>
      </div>
      <!-- End Modal Header -->

      <div class="evollu-form">

        <h4>{{ $t('ranu.records.info') }}</h4>

        <div class="row">
          <!-- Form Name Input -->
          <div class="col-12 col-md-6">
            <va-input
              id="name-input"
              v-model="form.data.name"
              :maxlength="75"
              :label="$t('ranu.records.full_name')"
              placeholder="---"
              :error="form.errors.has('name')"
              :error-messages="[$t(form.errors.get('name'))]"
              @focus="form.errors.clear('name')"
            />
          </div>
          <!-- End Form Name Input -->

          <!-- Form Process ID Input -->
          <div class="col-12 col-md-6">
            <va-input
              id="process-id-input"
              v-model="form.data.process_id"
              :maxlength="75"
              :label="$t('ranu.records.process_id')"
              placeholder="---"
              :error="form.errors.has('process_id')"
              :error-messages="[$t(form.errors.get('process_id'))]"
              @focus="form.errors.clear('process_id')"
            />
          </div>
          <!-- End Form Process ID Input -->
        </div>
        <!-- Form SNS Nr Input -->
        <div class="row">
          <div class="col-12 col-md-6">
            <va-input
              id="sns-number-input"
              v-model="form.data.sns_number"
              :maxlength="75"
              :label="$t('ranu.records.sns_number')"
              placeholder="---"
              :error="form.errors.has('sns_number')"
              :error-messages="[$t(form.errors.get('sns_number'))]"
              @focus="form.errors.clear('sns_number')"
            />
          </div>
          <!-- End Form SNS Nr Input -->

          <!-- Form Birthdate Input -->
          <div class="col-12 col-md-6">
            <DatePicker
              id="date-picker-birthdate"
              placeholder="---"
              v-model="form.data.birthdate"
              :label="$t('ranu.records.birthdate')"
              :config="singleDateDatepickerConfig"
              @input="form.errors.clear('birthdate')"/>
            <small v-show="form.errors.has('birthdate')" class="help text-danger">
              {{ $t(form.errors.get('birthdate')) }}
            </small>

          </div>
          <!-- End Form Birthdate Input -->
        </div>

        <div class="row">
          <!-- Form Risk Factor Input -->
          <div class="col-12 col-md-6">
            <label class="form-label">{{ $t('ranu.records.risk_factor') }}</label>
            <multiselect
              id="risk-factor-multiselect"
              v-model="form.data.risk_factor"
              :options="riskFactorsOptions"
              track-by="id"
              label="name"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              @input="form.errors.clear('risk_factor_id')"
            >
              <span slot="noResult">{{ $t('patients.noResults') }}</span>
              <template slot="clear" slot-scope="props">
                <div class="multiselect__clear" v-if="form.data.risk_factor"
                     @mousedown.prevent.stop="form.data.risk_factor = null"></div>
              </template>
              <template slot="option" slot-scope="props">
                <span>{{ props.option.name }}</span>
                <span v-if="props.option.description">{{ ` - ${props.option.description}` }}</span>
              </template>
            </multiselect>
            <div class="text-danger" v-if="form.errors.has('risk_factor_id')">
              <small>
                {{ $t(form.errors.get('risk_factor_id')) }}
              </small>
            </div>
          </div>
          <!-- End Form Risk Factor Input -->

          <!-- Form Entity Input -->
          <div class="col-12 col-md-6" v-if="entityOptions.length > 1 && !isUpdate">
            <label class="form-label">{{ $t('ranu.records.entity') }}</label>
            <multiselect
              id="entity-multiselect"
              v-model="form.data.entity"
              :options="entityOptions"
              track-by="id"
              label="name"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              @input="form.errors.clear('entity_id')"
            >
              <span slot="noResult">{{ $t('patients.noResults') }}</span>
            </multiselect>
            <div class="text-danger" v-if="form.errors.has('entity_id')">
              <small>
                {{ $t(form.errors.get('entity_id')) }}
              </small>
            </div>
          </div>
          <!-- End Form Entity Input -->
        </div>

        <div class="row">
          <div class="col-12 col-md-6">
            <va-checkbox
              id="checkbox-external"
              v-model="form.data.external"
              :label="$t('ranu.records.external')"
              :is-circle="true"
            />
          </div>

          <div class="col-12 col-md-6">
            <va-checkbox
              id="checkbox-isdead"
              v-model="form.data.status_id"
              :label="$t('ranu.records.died')"
              :is-circle="true"
            />
          </div>
        </div>

        <div class="row">
          <!-- Form Obs Input -->
          <div class="col-12">
            <va-input
              id="form-obs"
              v-model="form.data.obs"
              :maxlength="255"
              placeholder="---"
              :label="$t('ranu.records.obs')"
              @focus="form.errors.clear('obs')"
              :error="form.errors.has('obs')"
              :error-messages="[$t(form.errors.get('obs'))]"/>
          </div>
          <!-- End Form Obs Input -->
        </div>

        <!-- CONTACT DETAILS --->
        <h4>{{ $t('ranu.records.contacts_header') }}</h4>
        <div class="row">
          <!-- Mother Contact Name Input -->
          <div class="col-12 col-md-4">
            <va-input
              id="mother-name-input"
              v-model="form.data.mother"
              :maxlength="75"
              :label="$t('ranu.records.mother')"
              placeholder="---"
              :error="form.errors.has('mother')"
              :error-messages="[$t(form.errors.get('mother'))]"
              @focus="form.errors.clear('mother')"
            />
          </div>
          <!-- End Mother Contact Name Input -->

          <!-- Mother Contact Email Input -->
          <div class="col-12 col-md-4">
            <va-input
              id="mother-email-input"
              v-model="form.data.mother_email"
              :maxlength="75"
              :label="$t('ranu.records.mother_email')"
              placeholder="---"
              :error="form.errors.has('mother_email')"
              :error-messages="[$t(form.errors.get('mother_email'))]"
              @focus="form.errors.clear('mother_email')"
            />
          </div>
          <!-- End Mother Contact Email Input -->

          <!-- Mother Contact Phone Input -->
          <div class="col-12 col-md-4">
            <va-input
              id="mother-phone-input"
              v-model="form.data.mother_phone"
              :maxlength="75"
              :label="$t('ranu.records.mother_phone')"
              placeholder="---"
              :error="form.errors.has('mother_phone')"
              :error-messages="[$t(form.errors.get('mother_phone'))]"
              @focus="form.errors.clear('mother_phone')"
            />
          </div>
          <!-- End Mother Contact Phone Input -->
        </div>

        <div class="row">
          <!-- Father Contact Name Input -->
          <div class="col-12 col-md-4">
            <va-input
              id="father-name-input"
              v-model="form.data.father"
              :maxlength="75"
              :label="$t('ranu.records.father')"
              placeholder="---"
              :error="form.errors.has('father')"
              :error-messages="[$t(form.errors.get('father'))]"
              @focus="form.errors.clear('father')"
            />
          </div>
          <!-- End Father Contact Name Input -->

          <!-- Father Contact Email Input -->
          <div class="col-12 col-md-4">
            <va-input
              id="father-email-input"
              v-model="form.data.father_email"
              :maxlength="75"
              :label="$t('ranu.records.father_email')"
              placeholder="---"
              :error="form.errors.has('father_email')"
              :error-messages="[$t(form.errors.get('father_email'))]"
              @focus="form.errors.clear('father_email')"
            />
          </div>
          <!-- End Father Contact Email Input -->

          <!-- Father Contact Phone Input -->
          <div class="col-12 col-md-4">
            <va-input
              id="father-phone-input"
              v-model="form.data.father_phone"
              :maxlength="75"
              :label="$t('ranu.records.father_phone')"
              placeholder="---"
              :error="form.errors.has('father_phone')"
              :error-messages="[$t(form.errors.get('father_phone'))]"
              @focus="form.errors.clear('father_phone')"
            />
          </div>
          <!-- End Father Contact Phone Input -->
        </div>

        <div v-if="!isUpdate && !form.data.status_id" class="mb-5">

          <h4>{{ $t('ranu.records.hearingTest') }}</h4>

          <div class="row">
            <!-- Performed By Input -->
            <div class="col-12 col-md-6">
              <va-input
                id="performed-by-input"
                v-model="form.data.exam.performed_by"
                :maxlength="75"
                :label="$t('ranu.records.performed_by')"
                placeholder="---"
                :error="form.errors.has('performed_by')"
                :error-messages="[$t(form.errors.get('performed_by'))]"
                @focus="form.errors.clear('performed_by')"
              />
            </div>
            <!-- End Performed By Input -->

            <!-- Performed At Input -->
            <div class="col-12 col-md-6">
              <DatePicker
                id="date-picker-performed-at"
                placeholder="---"
                v-model="form.data.exam.performed_at"
                :label="$t('ranu.records.performed_at')"
                :config="singleDateDatepickerConfig"
                @input="form.errors.clear('performed_at')"/>
              <small v-show="form.errors.has('performed_at')" class="help text-danger">
                {{ $t(form.errors.get('performed_at')) }}
              </small>
            </div>
            <!-- End Performed At Input -->
          </div>

          <div class="row">
            <!-- Exam Type Multiselect -->
            <div class="col-12 col-md-6">
              <label class="form-label">{{ $t('ranu.records.exam_type') }}</label>
              <multiselect
                id="exam-type-right-multiselect"
                v-model="form.data.exam.exam_type"
                :options="examTypesOptions"
                track-by="id"
                label="name"
                select-label=""
                deselect-label="×"
                open-direction="bellow"
                placeholder="---"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
                @input="form.errors.clear('exam_type')"
              >
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
              </multiselect>
              <div class="text-danger" v-if="form.errors.has('exam_type')">
                <small>
                  {{ $t(form.errors.get('exam_type')) }}
                </small>
              </div>
            </div>
            <!-- End Exam Type Multiselect -->
          </div>

          <div class="row">
            <!-- Result Left Multiselect -->
            <div class="col-12 col-md-6">
              <label class="form-label">{{ $t('ranu.records.result_left') }}</label>
              <multiselect
                id="result-left-multiselect"
                v-model="form.data.exam.result_left"
                :options="statusesOptions"
                track-by="id"
                label="name"
                select-label=""
                deselect-label="×"
                open-direction="bellow"
                placeholder="---"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
                @input="form.errors.clear('result_left')"
              >
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
              </multiselect>
              <div class="text-danger" v-if="form.errors.has('result_left')">
                <small>
                  {{ $t(form.errors.get('result_left')) }}
                </small>
              </div>
            </div>
            <!-- End Result Left Multiselect -->

            <!-- Result Right Multiselect -->
            <div class="col-12 col-md-6">
              <label class="form-label">{{ $t('ranu.records.result_right') }}</label>
              <multiselect
                id="result-right-multiselect"
                v-model="form.data.exam.result_right"
                :options="statusesOptions"
                track-by="id"
                label="name"
                select-label=""
                deselect-label="×"
                open-direction="bellow"
                placeholder="---"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
                @input="form.errors.clear('result_right')"
                :disabled="rightResultIsDisabled"
              >
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
              </multiselect>
              <div class="text-danger" v-if="form.errors.has('result_right')">
                <small>
                  {{ $t(form.errors.get('result_right')) }}
                </small>
              </div>
            </div>
            <!-- End Result Right Multiselect -->
          </div>

          <div class="row">
            <!-- Form Exam Obs Input -->
            <div class="col-12">
              <va-input
                id="exam-obs"
                v-model="form.data.exam.obs"
                :maxlength="255"
                placeholder="---"
                :label="$t('ranu.records.obs')"/>
            </div>
            <!-- End Form Exam Obs Input -->
          </div>
        </div>

        <div v-if="!isUpdate && !form.data.status_id" class="mb-5">
          <!-- Upload files -->
          <h4>{{$t('ranu.records.upload_file')}}</h4>

          <div class="row">
            <div v-if="!form.file" class="col-12 text-center pt-5">
              <input ref="uploadFilesInput" id="upload-file-input" type="file" @input="onFileSelected" hidden
                    accept=".pdf, .png, .jpg, .jpeg">
              <va-button @click.prevent="uploadFile">
                <i class="fa fa-upload mr-1"></i> {{$t('ranu.records.upload_file')}}
              </va-button>
            </div>
            <div v-else class="col-12 text-center pt-5">
              <span class="font-weight-bold">{{form.file.name}}</span>
              <i class="fa fa-times ml-1 clickable" @click.prevent="removeFile"></i>
            </div>
            <small v-if="form.errors.has('file')" class="help text-danger">
              {{$t(form.errors.get('file'))}}
            </small>
          </div>
          <!-- End upload files -->
        </div>
      </div>
    </va-modal>
  </div>

</template>

<script>
import Errors from '@/utils/StandardErrors'
import { RecordsService } from '@/api/RanuService'
import Multiselect from 'vue-multiselect'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import { mapState } from 'vuex'
import { EMAIL_REGEX } from '@/utils/regex.js'
import DatePicker from '@/components/common/DatePicker'

const recordsInfoType = 100
const readWriteAccessType = 2

export default {
  props: {
    record: {
      type: Object,
      required: false,
    }
  },
  components: {
    DatePicker,
    Multiselect,
    ConfirmationModal
  },
  mixins: [],
  data () {
    return {
      singleDateDatepickerConfig: {
        dateFormat: 'Y-m-d H:i',
        mode: 'single',
        maxDate: this.moment().format('YYYY-MM-DD HH:mm'),
        enableTime: true,
        time_24hr: true,
        minuteIncrement: 1,
        locale: 'pt'
      },
      isOpen: false,
      isUpdate: false,
      rightResultIsDisabled: false,
      form: {
        file: null,
        data: {
          name: null,
          process_id: null,
          birthdate: null,
          entity: null,
          external: false,
          risk_factor: null,
          status_id: false,
          mother: null,
          mother_phone: null,
          mother_email: null,
          father: null,
          father_phone: null,
          father_email: null,
          obs: null,
          exam: {
            process_id: null,
            performed_by: null,
            performed_at: null,
            result_left: 1,
            result_right: 1,
            status: null,
            exam_type: null,
            obs: null
          }
        },
        options: {
          isDisabledResultRight: false
        },
        errors: new Errors()
      }
    }
  },
  mounted: function () {
    this.isOpen = true
    if (this.record) {
      this.prepareForUpdate()
    } else {
      this.fillForm()
    }
  },
  methods: {
    removeFile: function () {
      this.form.file = null
    },
    onFileSelected: function (event) {
      let files = event.target.files || event.dataTransfer.files
      if (!files.length) {
        return
      }
      this.form.file = files[0]
    },
    uploadFile: function () {
      this.$refs.uploadFilesInput.click()
    },
    getSelectedRecordContactByType: function (contactType) {
      return this.record.contacts.find(contact => contact.type === contactType)
    },
    fillForm: function () {
      this.$nextTick(() => {
        if (this.entityOptions.length) {
          this.form.data.entity = this.entityOptions[0]
        }
        if (this.examTypesOptions.length) {
          this.form.data.exam.exam_type = this.examTypesOptions[0]
        }
        this.form.data.exam.performed_by = this.$auth.user().title ? `${this.$auth.user().title} ${this.$auth.user().name}` : this.$auth.user().name
        this.form.data.exam.performed_at = this.moment().format('YYYY-MM-DD HH:mm')
        if (this.statusesOptions.length) {
          this.form.data.exam.result_left = this.statusesOptions[0]
          this.form.data.exam.result_right = this.statusesOptions[0]
        }
      })
    },
    submit: function () {
      if (!this.isSubmitingForm) {
        this.isSubmitingForm = true
        this.uploadFilesIfNecessary()
          .then(files => {
            return this.createOrUpdateRecord(this.getFormData(), files)
          })
          .catch(errors => {
            console.log(errors)
          })
          .finally(() => {
            this.isSubmitingForm = false
          })
      }
    },
    uploadFilesIfNecessary: function () {
      return new Promise((resolve, reject) => {
        if (!this.form.file) {
          resolve([])
        } else {
          let ranuFilesUrl = `${this.$store.state.ranu_server}/records/exams/files`
          let formData = new FormData()
          formData.append('file', this.form.file)

          this.$http.post(ranuFilesUrl, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
            .then(response => {
              let file = response.data.data
              resolve([file])
            })
            .catch(errors => {
              console.log(errors)
              let message = this.$t('ranu.records.errors.something_went_wrong')
              if (errors.hasOwnProperty('response') && errors.response.status === 422) {
                this.form.errors.record(errors.response.data.errors, 'ranu.files.errors.')
              }
              this.showToast(message, this.$store.state.errorToastOptions)
              reject(errors)
            })
        }
      })
    },
    createOrUpdateRecord: function (data, files = []) {
      return new Promise((resolve, reject) => {
        if (files.length > 0) {
          data.exam.exam_files = files.map(file => file.id)
        }
        let request = this.isUpdate ? RecordsService.update(this.record.id, data) : RecordsService.store(data)
        request
          .then(response => {
            let record = response.data.data
            this.showToast(this.isUpdate ? this.$t('ranu.records.record_updated') : this.$t('ranu.records.record_created'), this.$store.state.successToastOptions)
            this.$emit(this.isUpdate ? 'updated' : 'created', { record })
            this.$refs.recordModal.cancel()
          })
          .catch(errors => {
            console.log(errors)
            let message = this.$t('ranu.something_went_wrong')
            if (errors.hasOwnProperty('response')) {
              if (errors.response.status === 422) {
                this.form.errors.record(errors.response.data.errors, 'ranu.records.errors.')
                message = this.$t('ranu.records.form_contains_errors')
              } else if (errors.response.status === 403) {
                message = this.$t('ranu.records.missing_store_permissions')
              }
            }
            this.showToast(message, this.$store.state.errorToastOptions)
            reject(errors)
          })
      })
    },
    getFormData: function () {
      let data = JSON.parse(JSON.stringify(this.form.data))
      data.entity_id = data.entity ? data.entity.id : null
      delete data.entity
      data.risk_factor_id = data.risk_factor ? data.risk_factor.id : null
      delete data.risk_factor

      data.birthdate = data.birthdate ? this.$options.filters.utcTime(data.birthdate, 'YYYY-MM-DD HH:mm:ss') : null
      if (!data.status_id) {
        delete data.status_id
        if (!this.isUpdate) {
          if (data.exam.status) {
            data.exam.status_id = data.exam.status.id
          }
          if (data.exam.exam_type) {
            data.exam.exam_type_id = data.exam.exam_type.id
          }
          data.exam.performed_at = this.$options.filters.utcTime(data.exam.performed_at, 'YYYY-MM-DD HH:mm:ss')
          data.exam.result_left = data.exam.result_left.id
          data.exam.result_right = data.exam.result_right.id
          data.exam.obs = data.exam.obs
        } else {
          delete data.contacts
          delete data.exams
        }
      } else {
        delete data.exam
      }
      data.status_id = data.status_id ? 5 : null
      return data
    },
    close: function () {
      this.isOpen = false
      if (!this.isDestroyingRecord) {
        setTimeout(() => {
          this.$emit('modal-closed')
        }, 450)
      }
    },
    prepareForUpdate: function () {
      this.isUpdate = true
      this.form.data.name = this.record.name
      this.form.data.process_id = this.record.process_id
      this.form.data.sns_number = this.record.sns_number
      this.form.data.birthdate = this.record.birthdate
      this.form.data.risk_factor = this.record.risk_factor != null ? this.riskFactorsOptions.find(option => option.id === this.record.risk_factor.id) : null
      this.form.data.external = this.record.external
      this.form.data.obs = this.record.obs
      this.form.data.status_id = this.record.status ? this.record.status.id === 5 : false

      const mother = this.getSelectedRecordContactByType('mother')

      if (mother) {
        this.form.data.mother = this.contactsByType.mother.name
        this.form.data.mother_email = this.contactsByType.mother.email
        this.form.data.mother_phone = this.contactsByType.mother.phone
      }

      const father = this.getSelectedRecordContactByType('father')

      if (father) {
        this.form.data.father = this.contactsByType.father.name
        this.form.data.father_email = this.contactsByType.father.email
        this.form.data.father_phone = this.contactsByType.father.phone
      }
    }
  },
  computed: {
    ...mapState({
      'statusesOptions': (state) => state.ranu.statusesOptions,
      'examTypesOptions': (state) => state.ranu.examTypesOptions,
      'riskFactorsOptions': (state) => state.ranu.riskFactorsOptions
    }),
    contactsByType: function () {
      let types = ['mother', 'father']
      let contacts = {}

      types.forEach(type => {
        contacts[type] = this.getSelectedRecordContactByType(type)
      })

      return contacts
    },
    ranuService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.ranu_server) || null
    },
    recordPermissions: function () {
      return this.ranuService.user_permissions.filter(permission => permission.access_type === readWriteAccessType && permission.info_type === recordsInfoType)
    },
    entityOptions: function () {
      if (this.recordPermissions.some(permission => permission.entity_id === null)) {
        return this.ranuService.entities
      } else {
        return this.ranuService.entities.filter(entity => this.recordPermissions.some(permission => permission.entity_id === entity.id))
      }
    },
    dateInterval: function () {
      if (!this.form.data.start_date || !this.form.data.end_date) {
        return ''
      }

      switch (this.currentLanguage) {
      case 'pt':
        return this.form.data.start_date + ' a ' + this.form.data.end_date
      case 'es':
        return this.form.data.start_date + ' a ' + this.form.data.end_date
      default:
        return this.form.data.start_date + ' to ' + this.form.data.end_date
      }
    },
    invalidMotherEmail: function () {
      return this.form.data.mother_email ? !this.form.data.mother_email.match(EMAIL_REGEX) : false
    },
    invalidFatherEmail: function () {
      return this.form.data.father_email ? !this.form.data.father_email.match(EMAIL_REGEX) : false
    }
  },
  watch: {
    'form.data.exam.result_left': function (value) {
      if (value) {
        this.form.data.exam.result_right = value
        this.rightResultIsDisabled = value.applied_both
      }
    },
    'form.data.exam.result_right': function (value) {
      this.form.data.exam.result_left = value
    }
  }
}
</script>
